import { defineStore } from 'pinia';
import type {
    RustPermissionFramework,
    RustPermissionOpStatusValue,
    RustPermissionOpTypeValue,
    RustPermissionSyncStatus,
    RustPermissionUser,
} from '~/api/models';
import { RustPermissionsService } from '~/api/services/client';
import type { EffectivePermission, RustPermissionCounts, RustPermissionOpInput } from '~/api/services/client/rustPermissions';
import type { PluginSection } from '~/lib/rustPermissions';
import { buildPluginSections, normalizeGroupName, targetKeyFor } from '~/lib/rustPermissions';

export interface RustPermissionsSettingsState {
    enabled: boolean;
    status: RustPermissionSyncStatus;
    framework: RustPermissionFramework | null;
    moduleVersion: string | null;
    socketOnline: boolean;
    hashesMatch: boolean;
    lastImportAt: string | null;
    lastError: string | null;
}

export interface GroupState {
    /** Null while the group only exists as a staged creation. */
    id: string | null;
    name: string;
    title: string | null;
    rank: number;
    parent: string | null;
    permissions: string[];
    membersCount: number;
    isDefault: boolean;
}

export interface StagedOp {
    key: string;
    op: RustPermissionOpTypeValue;
    payload: Omit<RustPermissionOpInput, 'op'>;
    stagedAt: number;
}

export interface QueuedOpState {
    id: string;
    op: RustPermissionOpTypeValue;
    status: RustPermissionOpStatusValue;
    targetKey: string;
    payload: Record<string, any>;
    error: string | null;
    createdAt: string | null;
}

export interface PinnedUserState {
    steamId: string;
    nickname: string | null;
    permissions: string[];
    authLevel: number;
    groups: string[];
    effective: EffectivePermission[];
    /** True while the user exists only client-side (added by steam id). */
    phantom: boolean;
}

export interface GroupCellState {
    base: 'direct' | 'inherited' | 'none';
    /** Inheritance chain (membership group first) when base is inherited. */
    via?: string[];
    staged?: 'grant' | 'revoke';
    queued?: 'pending' | 'inflight';
    /** The value the queued op will produce once the server acks it. */
    queuedValue?: boolean;
    failed?: boolean;
}

export interface AckResult {
    id: string;
    result: string;
    error?: string | null;
    expanded?: string[];
}

export interface ImportProgressState {
    kind: string;
    received: number;
}

interface RustPermissionsStoreState {
    serverUuid: string | null;
    loading: boolean;
    loaded: boolean;
    matrixLoaded: boolean;
    settings: RustPermissionsSettingsState | null;
    counts: RustPermissionCounts | null;
    groups: Record<string, GroupState>;
    registeredPermissions: Record<string, string[]>;
    serverOps: Record<string, QueuedOpState>;
    stagedOps: Record<string, StagedOp>;
    pinnedUsers: Record<string, PinnedUserState>;
    pinnedOrder: string[];
    /** Auth-level staff, highest rank first. Auto-pinned, not persisted. */
    staffOrder: string[];
    importProgress: ImportProgressState | null;
    /** Bumped whenever server-side state may have changed (apply/acks/import). */
    applyGeneration: number;
}

// While an import is in flight the completion signal originates panel-side;
// the WS hello-relay usually delivers it instantly, but this poll guarantees
// the importing screen always resolves. Module-scoped: pinia state must stay
// serializable and only one server's page is open at a time.
let statusPollTimer: ReturnType<typeof setInterval> | undefined;

const STATUS_POLL_MS = 3000;

const stagedStorageKey = (serverUuid: string): string => `rust-permissions:staged:${serverUuid}`;
const pinsStorageKey = (serverUuid: string): string => `rust-permissions:pins:${serverUuid}`;

/** Matrix columns auto-pinned for staff; the Auth Levels tab lists the rest. */
const MAX_STAFF_PINS = 8;

// On a fast link the daemon can deliver a batch's acks over the websocket
// before the submitOps response tells this tab which op ids it created.
// Unmatched acks wait here until applyStaged registers the ops, then replay.
const earlyAckResults = new Map<string, AckResult>();

const EARLY_ACK_CAP = 500;

/**
 * The API parser camelCases nested objects, mangling op payload keys that
 * everything here (staging, target keys, mirror patching) treats as
 * snake_case. Restore the two multi-word keys ops can carry.
 */
const normalizeOpPayload = (payload: Record<string, any>): Record<string, any> => {
    const normalized = { ...payload };

    if (normalized.steamId !== undefined) {
        normalized.steam_id ??= normalized.steamId;
        delete normalized.steamId;
    }
    if (normalized.authLevel !== undefined) {
        normalized.auth_level ??= normalized.authLevel;
        delete normalized.authLevel;
    }

    return normalized;
};

/** The ops endpoint rejects bigger batches. */
const OPS_PER_REQUEST = 1000;

const OPEN_OP_STATUSES: RustPermissionOpStatusValue[] = ['pending', 'inflight', 'failed'];

export const useRustPermissionsStore = defineStore('rustPermissions', {
    state: (): RustPermissionsStoreState => ({
        serverUuid: null,
        loading: false,
        loaded: false,
        matrixLoaded: false,
        settings: null,
        counts: null,
        groups: {},
        registeredPermissions: {},
        serverOps: {},
        stagedOps: {},
        pinnedUsers: {},
        pinnedOrder: [],
        staffOrder: [],
        importProgress: null,
        applyGeneration: 0,
    }),

    getters: {
        enabled: (state): boolean => state.settings?.enabled ?? false,
        framework: (state): RustPermissionFramework | null => state.settings?.framework ?? null,
        isVanilla(): boolean {
            return this.framework === 'vanilla';
        },
        supportsGroups(): boolean {
            return this.framework === 'oxide' || this.framework === 'carbon';
        },

        /** Default group first, then rank descending, then name. */
        orderedGroups: (state): GroupState[] =>
            Object.values(state.groups).sort((a, b) => {
                if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
                if (a.rank !== b.rank) return b.rank - a.rank;
                return a.name.localeCompare(b.name);
            }),

        /** Matrix rows: registered permissions plus orphaned grants, by plugin. */
        pluginSections(state): PluginSection[] {
            const granted = new Set<string>();
            for (const group of Object.values(state.groups)) {
                for (const permission of group.permissions) granted.add(permission);
            }
            for (const user of Object.values(state.pinnedUsers)) {
                for (const permission of user.permissions) granted.add(permission);
            }

            return buildPluginSections(state.registeredPermissions, granted);
        },

        totalPermissionCount(): number {
            return this.pluginSections.reduce((total, section) => total + section.permissions.length, 0);
        },

        /**
         * Per group: every permission reachable through the parent chain
         * (excluding its own direct grants), mapped to the chain that
         * provides it, e.g. vip -> elite gives {"kits.elite": ["vip","elite"]}.
         */
        inheritedGroupPermissions(state): Record<string, Record<string, string[]>> {
            const result: Record<string, Record<string, string[]>> = {};

            for (const group of Object.values(state.groups)) {
                const inherited: Record<string, string[]> = {};
                const chain = [group.name];
                let current = state.groups[group.parent ?? ''];

                while (current && !chain.includes(current.name)) {
                    chain.push(current.name);
                    for (const permission of current.permissions) {
                        inherited[permission] ??= [...chain];
                    }
                    current = state.groups[current.parent ?? ''];
                }

                result[group.name] = inherited;
            }

            return result;
        },

        stagedList: (state): StagedOp[] => Object.values(state.stagedOps).sort((a, b) => a.stagedAt - b.stagedAt),
        stagedCount: (state): number => Object.keys(state.stagedOps).length,

        queuedOpsByTarget: (state): Record<string, QueuedOpState> => {
            const map: Record<string, QueuedOpState> = {};
            for (const op of Object.values(state.serverOps)) {
                if (op.status === 'pending' || op.status === 'inflight') map[op.targetKey] = op;
            }
            return map;
        },

        failedOps: (state): QueuedOpState[] =>
            Object.values(state.serverOps)
                .filter((op) => op.status === 'failed')
                .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),

        queuedCount(): number {
            return Object.keys(this.queuedOpsByTarget).length;
        },
    },

    actions: {
        async bootstrap(serverUuid: string): Promise<void> {
            if (this.serverUuid !== serverUuid) {
                this.stopStatusPolling();
                this.$reset();
                this.serverUuid = serverUuid;
                this.restoreLocalState();
            }

            this.loading = true;
            try {
                await this.refreshStatus();
            } finally {
                this.loading = false;
                this.loaded = true;
            }
        },

        async refreshStatus(): Promise<void> {
            const status = await RustPermissionsService.getStatus();

            this.settings = {
                enabled: status.settings.enabled,
                status: status.settings.status,
                framework: status.settings.framework,
                moduleVersion: status.settings.moduleVersion,
                socketOnline: status.settings.socketOnline,
                hashesMatch: status.settings.hashesMatch,
                lastImportAt: status.settings.lastImportAt,
                lastError: status.settings.lastError,
            };
            this.counts = status.counts;

            // Whenever the status lands on active, make sure the matrix is
            // there -- this is what completes the importing/waiting screens.
            if (this.enabled && this.supportsGroups && this.settings.status === 'active' && !this.matrixLoaded) {
                await this.refreshMatrix();
            }

            this.syncStatusPolling();
        },

        /**
         * Poll while the feature is in a transient state (waiting for the
         * server, importing). The WS relay is the fast path; this is the
         * fallback that guarantees those screens always resolve.
         */
        syncStatusPolling(): void {
            const transient = this.enabled && (this.settings?.status === 'pending_import' || this.settings?.status === 'importing');

            if (transient && statusPollTimer === undefined) {
                statusPollTimer = setInterval(() => {
                    void this.refreshStatus();
                }, STATUS_POLL_MS);
            } else if (!transient) {
                this.stopStatusPolling();
            }
        },

        stopStatusPolling(): void {
            if (statusPollTimer !== undefined) {
                clearInterval(statusPollTimer);
                statusPollTimer = undefined;
            }
        },

        async refreshMatrix(): Promise<void> {
            const matrix = await RustPermissionsService.getMatrix();

            const groups: Record<string, GroupState> = {};
            for (const group of matrix.groups) {
                groups[group.name] = {
                    id: group.id,
                    name: group.name,
                    title: group.title,
                    rank: group.rank,
                    parent: group.parent,
                    permissions: [...group.permissions],
                    membersCount: group.membersCount,
                    isDefault: group.isDefault,
                };
            }

            const serverOps: Record<string, QueuedOpState> = {};
            for (const op of matrix.ops) {
                if (!OPEN_OP_STATUSES.includes(op.status)) continue;
                serverOps[op.id] = {
                    id: op.id,
                    op: op.op,
                    status: op.status,
                    targetKey: op.targetKey,
                    payload: normalizeOpPayload(op.payload),
                    error: op.error,
                    createdAt: op.createdAt,
                };
            }

            this.groups = groups;
            this.registeredPermissions = matrix.registeredPermissions;
            this.serverOps = serverOps;
            this.matrixLoaded = true;

            await Promise.all([this.refreshPinnedUsers(), this.loadStaff()]);
        },

        async enable(): Promise<void> {
            await RustPermissionsService.enable();
            await this.refreshStatus();
        },

        async disable(): Promise<void> {
            await RustPermissionsService.disable();
            await this.refreshStatus();
        },

        async requestSync(): Promise<void> {
            await RustPermissionsService.sync();
        },

        // -- Cell state resolution -------------------------------------------------

        groupCellState(groupName: string, permission: string): GroupCellState {
            const group = this.groups[groupName];
            const key = `gp:${groupName}:${permission}`;

            const cell: GroupCellState = { base: 'none' };

            if (group?.permissions.includes(permission)) {
                cell.base = 'direct';
            } else {
                const via = this.inheritedGroupPermissions[groupName]?.[permission];
                if (via) {
                    cell.base = 'inherited';
                    cell.via = via;
                }
            }

            const staged = this.stagedOps[key];
            if (staged) cell.staged = staged.op === 'group.grant' ? 'grant' : 'revoke';

            const queued = this.queuedOpsByTarget[key];
            if (queued) {
                cell.queued = queued.status === 'inflight' ? 'inflight' : 'pending';
                cell.queuedValue = queued.op === 'group.grant';
            }

            if (Object.values(this.serverOps).some((op) => op.status === 'failed' && op.targetKey === key)) {
                cell.failed = true;
            }

            return cell;
        },

        userCellState(steamId: string, permission: string): GroupCellState {
            const user = this.pinnedUsers[steamId];
            const key = `up:${steamId}:${permission}`;

            const cell: GroupCellState = { base: 'none' };

            if (user?.permissions.includes(permission)) {
                cell.base = 'direct';
            } else {
                // Group-derived permissions, resolved against the live mirror
                // (not the server's effective snapshot) so editing a group
                // updates its members' columns immediately. Everyone is
                // implicitly in default.
                for (const groupName of new Set(['default', ...(user?.groups ?? [])])) {
                    const group = this.groups[groupName];
                    if (!group) continue;

                    if (group.permissions.includes(permission)) {
                        cell.base = 'inherited';
                        cell.via = [groupName];
                        break;
                    }

                    const chain = this.inheritedGroupPermissions[groupName]?.[permission];
                    if (chain) {
                        cell.base = 'inherited';
                        cell.via = chain;
                        break;
                    }
                }
            }

            const staged = this.stagedOps[key];
            if (staged) cell.staged = staged.op === 'user.grant' ? 'grant' : 'revoke';

            const queued = this.queuedOpsByTarget[key];
            if (queued) {
                cell.queued = queued.status === 'inflight' ? 'inflight' : 'pending';
                cell.queuedValue = queued.op === 'user.grant';
            }

            if (Object.values(this.serverOps).some((op) => op.status === 'failed' && op.targetKey === key)) {
                cell.failed = true;
            }

            return cell;
        },

        /**
         * The on/off value a cell would submit as: staged intent first, then
         * submitted-but-unacked ops, then the mirror.
         */
        groupCellValue(groupName: string, permission: string): boolean {
            const key = `gp:${groupName}:${permission}`;

            const staged = this.stagedOps[key];
            if (staged) return staged.op === 'group.grant';

            const queued = this.queuedOpsByTarget[key];
            if (queued) return queued.op === 'group.grant';

            return this.groups[groupName]?.permissions.includes(permission) ?? false;
        },

        userCellValue(steamId: string, permission: string): boolean {
            const key = `up:${steamId}:${permission}`;

            const staged = this.stagedOps[key];
            if (staged) return staged.op === 'user.grant';

            const queued = this.queuedOpsByTarget[key];
            if (queued) return queued.op === 'user.grant';

            return this.pinnedUsers[steamId]?.permissions.includes(permission) ?? false;
        },

        // -- Staging ---------------------------------------------------------------

        /**
         * Stage one idempotent change. Staging the value a target already
         * has on the server annihilates the entry instead of stacking a
         * pointless op -- toggling a cell twice leaves nothing behind.
         */
        stage(op: RustPermissionOpTypeValue, payload: Omit<RustPermissionOpInput, 'op'>, matchesBase: boolean): void {
            this.stageMany([{ op, payload, matchesBase }]);
        },

        /**
         * Stage a batch with a single localStorage write at the end --
         * bulk-pasting thousands of members must not serialize the whole
         * staged map once per entry.
         */
        stageMany(entries: { op: RustPermissionOpTypeValue; payload: Omit<RustPermissionOpInput, 'op'>; matchesBase?: boolean }[]): void {
            for (const { op, payload, matchesBase } of entries) {
                const key = targetKeyFor(op, payload);

                if (matchesBase) {
                    delete this.stagedOps[key];
                } else {
                    this.stagedOps[key] = { key, op, payload, stagedAt: Date.now() };
                }
            }

            this.persistLocalState();
        },

        setGroupPermission(groupName: string, permission: string, desired: boolean): void {
            // A queued op is the value the server is about to have; staging
            // must annihilate against that, or a queued grant could not be
            // countermanded back to the mirror value.
            const key = `gp:${groupName}:${permission}`;
            const queued = this.queuedOpsByTarget[key];
            const baseline = queued ? queued.op === 'group.grant' : (this.groups[groupName]?.permissions.includes(permission) ?? false);

            this.stage(desired ? 'group.grant' : 'group.revoke', { group: groupName, permission }, desired === baseline);
        },

        toggleGroupPermission(groupName: string, permission: string): void {
            this.setGroupPermission(groupName, permission, !this.groupCellValue(groupName, permission));
        },

        setUserPermission(steamId: string, permission: string, desired: boolean): void {
            const user = this.pinnedUsers[steamId];
            const key = `up:${steamId}:${permission}`;
            const queued = this.queuedOpsByTarget[key];
            const baseline = queued ? queued.op === 'user.grant' : (user?.permissions.includes(permission) ?? false);

            this.stage(
                desired ? 'user.grant' : 'user.revoke',
                { steam_id: steamId, permission, nickname: user?.nickname ?? null },
                desired === baseline,
            );
        },

        toggleUserPermission(steamId: string, permission: string): void {
            this.setUserPermission(steamId, permission, !this.userCellValue(steamId, permission));
        },

        stageMembership(steamId: string, groupName: string, member: boolean, nickname?: string | null): void {
            const baseMember = this.pinnedUsers[steamId]?.groups.includes(groupName) ?? false;

            this.stage(
                member ? 'user.add_group' : 'user.remove_group',
                { steam_id: steamId, group: groupName, nickname: nickname ?? this.pinnedUsers[steamId]?.nickname ?? null },
                this.pinnedUsers[steamId] ? member === baseMember : false,
            );
        },

        stageGroupCreate(name: string, title: string | null, rank: number): void {
            const normalized = normalizeGroupName(name);

            this.stage('group.create', { group: normalized, title, rank }, normalized in this.groups);

            // Phantom column so grants can be staged against it right away.
            if (!(normalized in this.groups)) {
                this.groups[normalized] = {
                    id: null,
                    name: normalized,
                    title,
                    rank,
                    parent: null,
                    permissions: [],
                    membersCount: 0,
                    isDefault: false,
                };
            }
        },

        stageGroupDelete(name: string): void {
            const group = this.groups[name];

            // Deleting a phantom simply discards its staged creation and
            // anything staged against it.
            if (group && group.id === null) {
                for (const staged of Object.values(this.stagedOps)) {
                    if (staged.payload.group === name) delete this.stagedOps[staged.key];
                }
                delete this.groups[name];
                this.persistLocalState();

                return;
            }

            this.stage('group.delete', { group: name }, group === undefined);
        },

        stageGroupMeta(name: string, meta: { title?: string | null; rank?: number; parent?: string | null }): void {
            const group = this.groups[name];

            if (meta.title !== undefined) {
                this.stage('group.set_title', { group: name, title: meta.title }, group !== undefined && group.title === meta.title);
            }
            if (meta.rank !== undefined) {
                this.stage('group.set_rank', { group: name, rank: meta.rank }, group !== undefined && group.rank === meta.rank);
            }
            if (meta.parent !== undefined) {
                this.stage('group.set_parent', { group: name, parent: meta.parent }, group !== undefined && group.parent === meta.parent);
            }
        },

        stageAuthLevel(steamId: string, level: number, nickname?: string | null): void {
            const base = this.pinnedUsers[steamId]?.authLevel;

            this.stage('user.set_auth_level', { steam_id: steamId, auth_level: level, nickname: nickname ?? null }, base === level);
        },

        discardStaged(key: string): void {
            const staged = this.stagedOps[key];
            delete this.stagedOps[key];

            // Discarding a phantom group's creation also drops the phantom.
            if (staged?.op === 'group.create' && this.groups[staged.payload.group ?? '']?.id === null) {
                delete this.groups[staged.payload.group ?? ''];
            }

            this.persistLocalState();
        },

        discardAllStaged(): void {
            for (const name of Object.keys(this.groups)) {
                if (this.groups[name]?.id === null) delete this.groups[name];
            }
            this.stagedOps = {};
            this.persistLocalState();
        },

        async applyStaged(): Promise<void> {
            const staged = this.stagedList;
            if (staged.length === 0) return;

            // Submitted sequentially so op order (and their ULIDs) matches
            // staging order across batches. Each accepted batch clears its
            // staged entries as it lands: a failure partway through leaves
            // only the unsubmitted changes staged.
            for (let offset = 0; offset < staged.length; offset += OPS_PER_REQUEST) {
                const batch = staged.slice(offset, offset + OPS_PER_REQUEST);
                const created = await RustPermissionsService.submitOps(batch.map((entry) => ({ op: entry.op, ...entry.payload })));

                for (const op of created) {
                    this.serverOps[op.id] = {
                        id: op.id,
                        op: op.op,
                        status: op.status,
                        targetKey: op.targetKey,
                        payload: normalizeOpPayload(op.payload),
                        error: op.error,
                        createdAt: op.createdAt,
                    };
                }

                for (const entry of batch) {
                    delete this.stagedOps[entry.key];
                }
                this.persistLocalState();

                // Replay any acks that beat this HTTP response over the socket.
                const raced = created.map((op) => earlyAckResults.get(op.id)).filter((result): result is AckResult => result !== undefined);
                if (raced.length > 0) {
                    for (const result of raced) earlyAckResults.delete(result.id);
                    this.applyAcksLocally(raced);
                }
            }

            this.applyGeneration += 1;
        },

        async cancelQueuedOp(id: string): Promise<void> {
            await RustPermissionsService.cancelOp(id);
            delete this.serverOps[id];
        },

        dismissFailedOp(id: string): void {
            delete this.serverOps[id];
        },

        // -- Pinned user columns ----------------------------------------------------

        /** A manually compared player goes to the front, ahead of staff. */
        async pinUser(steamId: string): Promise<void> {
            this.pinnedOrder = [steamId, ...this.pinnedOrder.filter((id) => id !== steamId)];
            if (!this.pinnedUsers[steamId]) await this.loadPinnedUser(steamId);
            this.persistLocalState();
        },

        unpinUser(steamId: string): void {
            this.pinnedOrder = this.pinnedOrder.filter((id) => id !== steamId);
            if (!this.staffOrder.includes(steamId)) delete this.pinnedUsers[steamId];
            this.persistLocalState();
        },

        /**
         * Auth-level staff (owners, then moderators) shown as pinned columns
         * by default -- highest auth level first, capped so a server with
         * hundreds of moderators doesn't become hundreds of matrix columns.
         * Refreshed from the server each matrix load rather than persisted.
         */
        async loadStaff(): Promise<void> {
            const response = await RustPermissionsService.getUsers({
                staff: true,
                per_page: MAX_STAFF_PINS,
                sort: '-auth_level',
            } as PaginatableRequest & {
                staff: boolean;
            });

            const staff = (response.data as unknown as RustPermissionUser[])
                .slice()
                .sort((a, b) => b.authLevel - a.authLevel || (a.nickname ?? a.steamId).localeCompare(b.nickname ?? b.steamId));

            this.staffOrder = staff.map((user) => user.steamId);
            await Promise.all(this.staffOrder.map((steamId) => this.loadPinnedUser(steamId)));
        },

        async loadPinnedUser(steamId: string): Promise<void> {
            try {
                const detail = await RustPermissionsService.getUser(steamId);
                this.pinnedUsers[steamId] = {
                    steamId,
                    nickname: detail.user.nickname,
                    permissions: [...detail.user.permissions],
                    authLevel: detail.user.authLevel,
                    groups: detail.user.groups ?? [],
                    effective: detail.effective,
                    phantom: false,
                };
            } catch {
                // Not in the mirror yet: a phantom column that changes can
                // be staged against (the first grant creates the user).
                this.pinnedUsers[steamId] = {
                    steamId,
                    nickname: null,
                    permissions: [],
                    authLevel: 0,
                    groups: [],
                    effective: [],
                    phantom: true,
                };
            }
        },

        async refreshPinnedUsers(): Promise<void> {
            await Promise.all(this.pinnedOrder.map((steamId) => this.loadPinnedUser(steamId)));
        },

        // -- Live updates from the daemon relay --------------------------------------

        async handleDaemonEvent(payload: { type: string; data?: any }): Promise<void> {
            switch (payload.type) {
                case 'state':
                    await this.refreshStatus();
                    break;
                case 'import-progress':
                    this.importProgress = { kind: payload.data?.kind ?? '', received: payload.data?.received ?? 0 };
                    break;
                case 'import-complete':
                    this.importProgress = null;
                    await this.refreshStatus();
                    if (this.supportsGroups) await this.refreshMatrix();
                    this.applyGeneration += 1;
                    break;
                case 'delta':
                    for (const delta of payload.data?.deltas ?? []) {
                        this.applyDeltaLocally(delta);
                    }
                    break;
                case 'op-acks':
                    this.applyAcksLocally(payload.data?.results ?? []);
                    break;
            }
        },

        /**
         * Keep the open matrix live when someone edits in-game. Only groups
         * and pinned users are patched -- everything else reloads lazily.
         */
        applyDeltaLocally(delta: Record<string, any>): void {
            const group = typeof delta.group === 'string' ? delta.group.toLowerCase() : undefined;
            const permission = typeof delta.permission === 'string' ? delta.permission.toLowerCase() : undefined;
            const steamId = typeof delta.steam_id === 'string' ? delta.steam_id : undefined;

            switch (delta.action) {
                case 'group.created':
                    if (group && !(group in this.groups)) {
                        this.groups[group] = {
                            id: '',
                            name: group,
                            title: delta.title ?? null,
                            rank: delta.rank ?? 0,
                            parent: null,
                            permissions: [],
                            membersCount: 0,
                            isDefault: false,
                        };
                    }
                    break;
                case 'group.deleted':
                    if (group) delete this.groups[group];
                    break;
                case 'group.title_set':
                    if (group && this.groups[group]) this.groups[group].title = delta.title ?? null;
                    break;
                case 'group.rank_set':
                    if (group && this.groups[group]) this.groups[group].rank = delta.rank ?? 0;
                    break;
                case 'group.parent_set':
                    if (group && this.groups[group]) this.groups[group].parent = delta.parent ? String(delta.parent).toLowerCase() : null;
                    break;
                case 'group.perm_granted':
                    if (group && permission && this.groups[group] && !this.groups[group].permissions.includes(permission)) {
                        this.groups[group].permissions.push(permission);
                        this.groups[group].permissions.sort();
                    }
                    break;
                case 'group.perm_revoked':
                    if (group && permission && this.groups[group]) {
                        this.groups[group].permissions = this.groups[group].permissions.filter((entry) => entry !== permission);
                    }
                    break;
                case 'user.group_added':
                    if (group && this.groups[group]) this.groups[group].membersCount += 1;
                    if (steamId && group && this.pinnedUsers[steamId] && !this.pinnedUsers[steamId].groups.includes(group)) {
                        this.pinnedUsers[steamId].groups.push(group);
                    }
                    break;
                case 'user.group_removed':
                    if (group && this.groups[group]) this.groups[group].membersCount = Math.max(0, this.groups[group].membersCount - 1);
                    if (steamId && group && this.pinnedUsers[steamId]) {
                        this.pinnedUsers[steamId].groups = this.pinnedUsers[steamId].groups.filter((entry) => entry !== group);
                    }
                    break;
                case 'user.perm_granted':
                    if (steamId && permission && this.pinnedUsers[steamId] && !this.pinnedUsers[steamId].permissions.includes(permission)) {
                        this.pinnedUsers[steamId].permissions.push(permission);
                        this.pinnedUsers[steamId].permissions.sort();
                    }
                    break;
                case 'user.perm_revoked':
                    if (steamId && permission && this.pinnedUsers[steamId]) {
                        this.pinnedUsers[steamId].permissions = this.pinnedUsers[steamId].permissions.filter((entry) => entry !== permission);
                    }
                    break;
                case 'user.auth_level_set':
                    if (steamId && this.pinnedUsers[steamId]) this.pinnedUsers[steamId].authLevel = delta.auth_level ?? 0;
                    break;
                case 'permission.registered':
                    if (permission) {
                        const plugin = permission.includes('.') ? permission.slice(0, permission.indexOf('.')) : permission;
                        this.registeredPermissions[plugin] ??= [];
                        if (!this.registeredPermissions[plugin].includes(permission)) {
                            this.registeredPermissions[plugin].push(permission);
                            this.registeredPermissions[plugin].sort();
                        }
                    }
                    break;
            }

            // An in-game edit that produced exactly what a staged change
            // wanted makes that staged change moot -- drop it quietly.
            this.pruneSatisfiedStaged();
        },

        applyAcksLocally(results: AckResult[]): void {
            let applied = false;

            for (const result of results) {
                const op = this.serverOps[result.id];
                if (!op) {
                    if (earlyAckResults.size >= EARLY_ACK_CAP) earlyAckResults.clear();
                    earlyAckResults.set(result.id, result);
                    continue;
                }

                applied = true;

                if (result.result === 'error') {
                    op.status = 'failed';
                    op.error = result.error ?? null;
                    continue;
                }

                this.applyOpEffectLocally(op, result.expanded);
                delete this.serverOps[result.id];
            }

            if (applied) this.applyGeneration += 1;
        },

        applyOpEffectLocally(op: QueuedOpState, expanded?: string[]): void {
            const payload = op.payload;
            const permissions = expanded && expanded.length > 0 ? expanded.map((entry) => entry.toLowerCase()) : [payload.permission];

            switch (op.op) {
                case 'group.create':
                    this.applyDeltaLocally({ action: 'group.created', ...payload });
                    break;
                case 'group.delete':
                    this.applyDeltaLocally({ action: 'group.deleted', ...payload });
                    break;
                case 'group.set_title':
                    this.applyDeltaLocally({ action: 'group.title_set', ...payload });
                    break;
                case 'group.set_rank':
                    this.applyDeltaLocally({ action: 'group.rank_set', ...payload });
                    break;
                case 'group.set_parent':
                    this.applyDeltaLocally({ action: 'group.parent_set', ...payload });
                    break;
                case 'group.grant':
                    for (const permission of permissions) {
                        this.applyDeltaLocally({ action: 'group.perm_granted', group: payload.group, permission });
                    }
                    break;
                case 'group.revoke':
                    for (const permission of permissions) {
                        this.applyDeltaLocally({ action: 'group.perm_revoked', group: payload.group, permission });
                    }
                    break;
                case 'user.add_group':
                    this.applyDeltaLocally({ action: 'user.group_added', ...payload });
                    break;
                case 'user.remove_group':
                    this.applyDeltaLocally({ action: 'user.group_removed', ...payload });
                    break;
                case 'user.grant':
                    for (const permission of permissions) {
                        this.applyDeltaLocally({ action: 'user.perm_granted', steam_id: payload.steam_id, permission });
                    }
                    break;
                case 'user.revoke':
                    for (const permission of permissions) {
                        this.applyDeltaLocally({ action: 'user.perm_revoked', steam_id: payload.steam_id, permission });
                    }
                    break;
                case 'user.set_auth_level':
                    this.applyDeltaLocally({ action: 'user.auth_level_set', ...payload });
                    break;
            }
        },

        pruneSatisfiedStaged(): void {
            let changed = false;

            for (const staged of Object.values(this.stagedOps)) {
                const payload = staged.payload;
                let satisfied = false;

                switch (staged.op) {
                    case 'group.grant':
                        satisfied = this.groups[payload.group ?? '']?.permissions.includes(payload.permission ?? '') ?? false;
                        break;
                    case 'group.revoke':
                        satisfied = payload.group! in this.groups && !this.groups[payload.group!].permissions.includes(payload.permission ?? '');
                        break;
                    case 'user.grant':
                        satisfied = this.pinnedUsers[payload.steam_id ?? '']?.permissions.includes(payload.permission ?? '') ?? false;
                        break;
                    case 'user.revoke': {
                        const user = this.pinnedUsers[payload.steam_id ?? ''];
                        satisfied = user !== undefined && !user.permissions.includes(payload.permission ?? '');
                        break;
                    }
                }

                if (satisfied) {
                    delete this.stagedOps[staged.key];
                    changed = true;
                }
            }

            if (changed) this.persistLocalState();
        },

        // -- Local persistence --------------------------------------------------------

        persistLocalState(): void {
            if (!this.serverUuid) return;

            try {
                localStorage.setItem(stagedStorageKey(this.serverUuid), JSON.stringify(Object.values(this.stagedOps)));
                localStorage.setItem(pinsStorageKey(this.serverUuid), JSON.stringify(this.pinnedOrder));
            } catch {
                // Quota/private-mode failures just lose persistence, not function.
            }
        },

        restoreLocalState(): void {
            if (!this.serverUuid) return;

            try {
                const staged = JSON.parse(localStorage.getItem(stagedStorageKey(this.serverUuid)) ?? '[]') as StagedOp[];
                for (const entry of staged) {
                    if (entry?.key && entry?.op) this.stagedOps[entry.key] = entry;
                }

                const pins = JSON.parse(localStorage.getItem(pinsStorageKey(this.serverUuid)) ?? '[]') as string[];
                this.pinnedOrder = Array.isArray(pins) ? pins.slice(0, 8) : [];
            } catch {
                this.stagedOps = {};
                this.pinnedOrder = [];
            }
        },
    },
});
